"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
const button_1 = require("@atlaskit/button");
const trash_1 = __importDefault(require("@atlaskit/icon/glyph/trash"));
const React = __importStar(require("react"));
const button_2 = require("../button");
const form_1 = require("../form");
const dynamic_table_1 = require("../table/dynamic-table");
const editable_context_1 = require("./editable-context");
const { ErrorMessage } = require("@atlaskit/form");
const TextField = require("@atlaskit/textfield").default;
/**
 * Implements functionality to edit array of items on client side, that includes
 * adding and conditional editing/removing items.
 */
class EditableDynamicTable extends React.Component {
    constructor() {
        super(...arguments);
        /** Generates table rows using provided configuration. */
        this.getTableRows = (fields, fieldProps, isEdit, isSubmitting, allowDelete) => fieldProps.value.map((item, index) => ({
            key: `row-${item.key}`,
            cells: this.getRowCells(fields, fieldProps, item, index, isEdit, isSubmitting, allowDelete)
        }));
    }
    /** Creates new array item and appends a row with it to the table. */
    addRow(fieldProps, createNewItem) {
        fieldProps.onChange([...fieldProps.value, createNewItem()]);
    }
    /** Removes item from the underlying array at index specified. */
    removeItemAtIndex(fieldProps, index) {
        const newValue = fieldProps.value.filter((_, i) => i !== index);
        fieldProps.onChange(this.getChangedOrDefault(newValue));
    }
    /**
     * Handles edit events for the array items and propagates changes to the
     * underlying array.
     */
    updateValue(fieldProps, newValue, field, index) {
        // Need to create a new array because final-form tracks state by reference.
        const items = fieldProps.value.map((item, i) => {
            if (i !== index) {
                return item;
            }
            return Object.assign(Object.assign({}, item), { [field]: newValue });
        });
        fieldProps.onChange(this.getChangedOrDefault(items));
    }
    /**
     * Compares mutated array with default one and returns default one in case they
     * are equal. Need to do it because final-form tracks state by reference and we
     * want to disable `Save` button when changes are reverted.
     */
    getChangedOrDefault(changed) {
        if (changed.length !== this.props.defaultValue.length) {
            return changed;
        }
        if (changed.length === 0) {
            return this.props.defaultValue;
        }
        const areDifferent = changed.some((item, idx) => {
            const defaultItem = this.props.defaultValue[idx];
            return this.props.fields.some(field => defaultItem[field.key] !== item[field.key]);
        });
        return areDifferent ? changed : this.props.defaultValue;
    }
    /** Prepares all the data required to render table header. */
    getTableHeadInfo(fields) {
        const info = {
            head: {
                cells: []
            }
        };
        fields.reduce((cells, field) => {
            const cell = {
                key: field.key,
                content: field.headContent,
                isSortable: field.isSortable,
                shouldTruncate: field.shouldTruncate,
                width: field.columnWidth
            };
            cells.push(cell);
            if (field.isDefaultSortKey && info.defaultSortKey !== undefined) {
                throw new Error("There should be only one default sort key");
            }
            if (field.isDefaultSortKey) {
                info.defaultSortKey = field.key;
                info.defaultSortOrder = field.defaultSortOrder;
            }
            return cells;
        }, info.head.cells);
        return info;
    }
    /** Generates cells for the given row. */
    getRowCells(fields, fieldProps, item, index, isEdit, isSubmitting, allowDelete) {
        const cells = fields.map(field => {
            const validationError = field.validate ? field.validate(item) : undefined;
            const renderCell = field.shouldCellRender
                ? field.shouldCellRender(item, index)
                : true;
            const onChange = (e) => {
                const value = field.getValue ? field.getValue(e) : e.target.value;
                this.updateValue(fieldProps, value, field.key, index);
            };
            let cellContent;
            if (renderCell && isEdit) {
                if (field.editableContent) {
                    cellContent = React.cloneElement(field.editableContent, {
                        isDisabled: isSubmitting,
                        defaultValue: item[field.key] || undefined,
                        defaultChecked: item[field.key] || false,
                        onChange
                    });
                }
                else {
                    cellContent = (React.createElement(TextField, { isDisabled: isSubmitting, defaultValue: item[field.key] || undefined, onChange: onChange }));
                }
                cellContent = (React.createElement(React.Fragment, null,
                    cellContent,
                    validationError && React.createElement(ErrorMessage, null, validationError)));
            }
            if (renderCell && !isEdit) {
                if (field.viewContent) {
                    cellContent = React.cloneElement(field.viewContent, {
                        value: item[field.key] || undefined
                    });
                }
                else {
                    cellContent = item[field.key] || undefined;
                }
            }
            return {
                key: `${item[field.key]}-${index}-${field.key}-cell`,
                content: cellContent
            };
        });
        if (isEdit && allowDelete && allowDelete(item)) {
            const deleteButtonCell = {
                key: `${index}-delete-cell`,
                content: (React.createElement(button_2.Button, { appearance: "subtle", disabled: isSubmitting, onClick: () => {
                        this.removeItemAtIndex(fieldProps, index);
                    }, iconBefore: React.createElement(trash_1.default, { label: "delete" }) }))
            };
            cells.push(deleteButtonCell);
        }
        return cells;
    }
    render() {
        const { allowDelete, createNewItem, defaultValue, fields, name, validateValue, emptyView, defaultEditMode, rowsPerPage, onSetPage } = this.props;
        const headInfo = this.getTableHeadInfo(fields);
        const table = (React.createElement(editable_context_1.EditableContext.Consumer, null, ({ isEditMode, isSubmitting }) => (
        /**
         * Currently Atlas Kit's form doesn't support final-form-arrays, so we
         * working it around by pushing the entire array into a single field.
         */
        React.createElement(form_1.Field, { name: name, defaultValue: defaultValue, validate: validateValue }, ({ fieldProps, error }) => (React.createElement(React.Fragment, null,
            React.createElement(dynamic_table_1.DynamicTable, { head: headInfo.head, rowsPerPage: rowsPerPage, onSetPage: onSetPage, rows: this.getTableRows(fields, fieldProps, isEditMode, isSubmitting, allowDelete), 
                // Don't sort in edit mode to prevent sorting when user inputs data
                defaultSortKey: isEditMode ? undefined : headInfo.defaultSortKey, defaultSortOrder: headInfo.defaultSortOrder, emptyView: emptyView }),
            isEditMode ? (React.createElement(button_1.ButtonGroup, null,
                createNewItem ? (React.createElement(button_2.Button, { disabled: isSubmitting, onClick: () => this.addRow(fieldProps, createNewItem) }, this.props.addButtonText
                    ? this.props.addButtonText
                    : "Add Row")) : (undefined),
                React.createElement(button_2.Button, { disabled: isSubmitting, onClick: () => fieldProps.onChange(defaultValue) }, this.props.resetButtonText
                    ? this.props.resetButtonText
                    : "Reset"),
                error && React.createElement(ErrorMessage, null, error))) : (undefined)))))));
        return defaultEditMode ? (React.createElement(editable_context_1.EditableContext.Provider, { value: {
                isEditMode: true,
                isDirty: false,
                isSubmitting: false
            } }, table)) : (table);
    }
}
exports.EditableDynamicTable = EditableDynamicTable;
